import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  cn,
  SegmentedControl,
  Skeleton,
} from '@up2stay/up2promo-ui';
import {
  ProductScanSource,
  PeriodChangeDirection,
  ProfileViewSource,
  SentimentTrendGranularity,
  type ProfileUsageDto,
} from '@up2stay/up2promo-api-client';
import { Check, Eye, Info, Link2, Wallet } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { Area, ComposedChart, XAxis, YAxis } from 'recharts';

import { formatTrendLabel } from '@/core/components/reputation/reputation-bits';
import type { ProfileUsageRangeDaysValue } from '@/core/hooks/use-profile-usage';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

const VIEW_SOURCE_ORDER = [
  ProfileViewSource.WALLET,
  ProfileViewSource.PROFILE_QR,
  ProfileViewSource.DIRECT,
  ProfileViewSource.PRODUCT,
] as const;

const VIEW_SOURCE_FILL: Record<(typeof VIEW_SOURCE_ORDER)[number], string> = {
  [ProfileViewSource.WALLET]:
    'color-mix(in oklab, var(--profile-series) 100%, transparent)',
  [ProfileViewSource.PROFILE_QR]:
    'color-mix(in oklab, var(--profile-series) 68%, transparent)',
  [ProfileViewSource.DIRECT]:
    'color-mix(in oklab, var(--profile-series) 42%, transparent)',
  [ProfileViewSource.PRODUCT]:
    'color-mix(in oklab, var(--profile-series) 22%, transparent)',
};

export type ProfilesUsageDict = {
  title: string;
  description: string;
  rangeDays7: string;
  rangeDays30: string;
  rangeAria: string;
  period: {
    vsPrevious: string;
    vsPreviousNew: string;
    vsPreviousFlat: string;
  };
  avgPerDayLast: string;
  lastRelative: string;
  lastJustNow: string;
  sourceTitle: string;
  sourceWallet: string;
  sourceWalletHint: string;
  sourceQr: string;
  sourceQrHint: string;
  sourceDirect: string;
  sourceDirectHint: string;
  sourceProduct: string;
  sourceProductQrNfc: string;
  footnote: string;
  empty: string;
  emptyDescription: string;
  emptyCopyLink: string;
  emptyCopied: string;
  emptyAddToWallet: string;
};

function formatPeriodChangeLabel(
  change: ProfileUsageDto['periodChange'] | undefined,
  templates: ProfilesUsageDict['period'],
): string {
  if (!change) return '';
  if (change.direction === PeriodChangeDirection.NEW) {
    return templates.vsPreviousNew;
  }
  if (
    change.direction === PeriodChangeDirection.FLAT ||
    change.percent == null
  ) {
    return templates.vsPreviousFlat;
  }
  const sign = change.percent > 0 ? '+' : '';
  const pct = `${sign}${change.percent.toFixed(Math.abs(change.percent) >= 10 ? 0 : 1)}%`;
  return templates.vsPrevious.replace('{pct}', pct);
}

/**
 * Relative "last viewed" phrase. Intl already says "vor 9 Minuten" /
 * "gestern", so the template must not add its own "vor" (DEV-536); under a
 * minute reads "gerade eben" instead of "in dieser Minute".
 */
export function formatRelativeLastViewed(
  lastViewedAt: string | null | undefined,
  locale: string,
  template: string,
  justNow: string,
  now: number = Date.now(),
): string | null {
  if (!lastViewedAt) return null;
  const then = new Date(lastViewedAt).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = now - then;
  const minutes = Math.max(0, Math.round(diffMs / 60_000));
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  let relative: string;
  if (minutes < 1) {
    relative = justNow;
  } else if (minutes < 60) {
    relative = rtf.format(-minutes, 'minute');
  } else if (minutes < 60 * 48) {
    relative = rtf.format(-Math.round(minutes / 60), 'hour');
  } else {
    relative = rtf.format(-Math.round(minutes / (60 * 24)), 'day');
  }
  return template.replace('{relative}', relative);
}

export function ProfilesUsageCard({
  dict,
  usage,
  isLoading,
  rangeDays,
  onRangeChange,
  locale,
  copyLinkUrl,
  walletEnabled,
  walletReady,
  isAddingToWallet,
  onAddToWallet,
  onCopyLink,
}: {
  dict: ProfilesUsageDict;
  usage: ProfileUsageDto | undefined;
  isLoading: boolean;
  rangeDays: ProfileUsageRangeDaysValue;
  onRangeChange: (days: ProfileUsageRangeDaysValue) => void;
  locale: string;
  copyLinkUrl: string | null;
  walletEnabled: boolean;
  walletReady: boolean;
  isAddingToWallet: boolean;
  onAddToWallet: () => void;
  onCopyLink: () => void;
}) {
  const total = usage?.total ?? 0;
  const hasViews = total > 0;
  const periodChangeLabel = formatPeriodChangeLabel(
    usage?.periodChange,
    dict.period,
  );

  const sourceCounts = useMemo(
    () => new Map((usage?.sources ?? []).map((row) => [row.source, row.count])),
    [usage?.sources],
  );
  const productSourceCounts = useMemo(
    () =>
      new Map(
        (usage?.productSources ?? []).map((row) => [row.source, row.count]),
      ),
    [usage?.productSources],
  );

  const sourceMeta: Record<
    (typeof VIEW_SOURCE_ORDER)[number],
    { label: string; hint: string }
  > = {
    [ProfileViewSource.WALLET]: {
      label: dict.sourceWallet,
      hint: dict.sourceWalletHint,
    },
    [ProfileViewSource.PROFILE_QR]: {
      label: dict.sourceQr,
      hint: dict.sourceQrHint,
    },
    [ProfileViewSource.DIRECT]: {
      label: dict.sourceDirect,
      hint: dict.sourceDirectHint,
    },
    [ProfileViewSource.PRODUCT]: {
      label: dict.sourceProduct,
      hint: dict.sourceProductQrNfc
        .replace(
          '{qr}',
          String(productSourceCounts.get(ProductScanSource.QR) ?? 0),
        )
        .replace(
          '{nfc}',
          String(productSourceCounts.get(ProductScanSource.NFC) ?? 0),
        ),
    },
  };

  const chartConfig = useMemo(
    () =>
      ({
        views: {
          label: dict.title,
          color: 'var(--profile-series)',
        },
      }) satisfies ChartConfig,
    [dict.title],
  );

  const chartData = useMemo(
    () =>
      (usage?.series ?? []).map((point) => ({
        ...point,
        label: formatTrendLabel(
          point.date,
          SentimentTrendGranularity.DAY,
          locale,
        ),
      })),
    [locale, usage?.series],
  );
  const maxViews = Math.max(...chartData.map((p) => p.views), 1);
  const avgPerDay = rangeDays > 0 ? total / rangeDays : 0;
  const lastRelative = formatRelativeLastViewed(
    usage?.lastViewedAt,
    locale,
    dict.lastRelative,
    dict.lastJustNow,
  );
  const avgLastLine = dict.avgPerDayLast
    .replace(
      '{avg}',
      avgPerDay.toLocaleString(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0,
      }),
    )
    .replace('{last}', lastRelative ?? '—');

  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopyLink = () => {
    if (!copyLinkUrl) return;
    void navigator.clipboard.writeText(copyLinkUrl).then(() => {
      void triggerTapHaptic();
      setLinkCopied(true);
      onCopyLink();
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  return (
    <Card className="min-w-0">
      <CardHeader className="flex-row items-start justify-between gap-3 pb-0 max-md:p-5 md:p-6">
        <div className="min-w-0 flex-1">
          <CardTitle className="flex items-center gap-2.5 text-[15px] font-bold leading-tight">
            <Eye
              className="size-[17px] shrink-0 text-[color:var(--ghost-accent-fg)]"
              aria-hidden
            />
            {dict.title}
          </CardTitle>
          <p className="mt-1.5 text-pretty text-[12.5px] leading-normal text-muted-foreground">
            {dict.description}
          </p>
        </div>
        <SegmentedControl
          ariaLabel={dict.rangeAria}
          value={String(rangeDays) as `${ProfileUsageRangeDaysValue}`}
          onChange={(next) =>
            onRangeChange(Number(next) as ProfileUsageRangeDaysValue)
          }
          options={[
            { value: '7', label: dict.rangeDays7 },
            { value: '30', label: dict.rangeDays30 },
          ]}
        />
      </CardHeader>
      <CardContent className="space-y-[18px] p-5 pt-4 md:p-6 md:pt-[18px]">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-14 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-xl" />
          </div>
        ) : hasViews ? (
          <>
            <div className="grid grid-cols-1 items-end gap-7 md:grid-cols-[minmax(11rem,14rem)_minmax(0,1fr)]">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-2.5">
                  <span className="text-[34px] font-bold leading-none tracking-tight text-foreground">
                    {total.toLocaleString(locale)}
                  </span>
                  {periodChangeLabel ? (
                    <span
                      className={cn(
                        'text-[12.5px] font-medium tabular-nums',
                        usage?.periodChange.direction ===
                          PeriodChangeDirection.UP &&
                          'text-[color:var(--ok-fg)]',
                        usage?.periodChange.direction ===
                          PeriodChangeDirection.DOWN && 'text-destructive',
                        (usage?.periodChange.direction ===
                          PeriodChangeDirection.FLAT ||
                          usage?.periodChange.direction ===
                            PeriodChangeDirection.NEW) &&
                          'text-muted-foreground',
                      )}
                    >
                      {periodChangeLabel}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1.5 text-[12px] text-muted-foreground">
                  {avgLastLine}
                </p>
              </div>
              <div className="min-w-0">
                <ChartContainer
                  config={chartConfig}
                  className="aspect-auto h-14 w-full"
                >
                  <ComposedChart
                    accessibilityLayer
                    data={chartData}
                    margin={{ left: 0, right: 0, top: 4, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="profileUsageFill"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="var(--profile-series)"
                          stopOpacity={0.22}
                        />
                        <stop
                          offset="100%"
                          stopColor="var(--profile-series)"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={4}
                      minTickGap={48}
                      interval="preserveStartEnd"
                      className="text-[11px]"
                    />
                    <YAxis hide domain={[0, Math.ceil(maxViews * 1.15)]} />
                    <ChartTooltip
                      cursor={false}
                      content={(tooltipProps) => (
                        <ChartTooltipContent
                          {...(tooltipProps as ComponentProps<
                            typeof ChartTooltipContent
                          >)}
                          indicator="line"
                          labelKey="label"
                        />
                      )}
                    />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name={dict.title}
                      stroke="var(--profile-series)"
                      strokeWidth={2}
                      fill="url(#profileUsageFill)"
                    />
                  </ComposedChart>
                </ChartContainer>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-border/50 pt-4">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {dict.sourceTitle}
              </div>
              <div className="flex h-1.5 w-full overflow-hidden rounded-[3px]">
                {VIEW_SOURCE_ORDER.map((source) => {
                  const count = sourceCounts.get(source) ?? 0;
                  if (count <= 0) return null;
                  return (
                    <span
                      key={source}
                      className="min-w-0"
                      style={{
                        flex: `${count} 1 0`,
                        background: VIEW_SOURCE_FILL[source],
                      }}
                    />
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {VIEW_SOURCE_ORDER.map((source) => {
                  const count = sourceCounts.get(source) ?? 0;
                  const share =
                    total > 0 ? Math.round((count / total) * 100) : 0;
                  const meta = sourceMeta[source] ?? {
                    label: source,
                    hint: '',
                  };
                  return (
                    <div key={source} className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground">
                        <span
                          className="size-2 shrink-0 rounded-full"
                          style={{ background: VIEW_SOURCE_FILL[source] }}
                          aria-hidden
                        />
                        <span className="truncate">{meta.label}</span>
                      </div>
                      <div className="mt-1.5 flex items-baseline gap-1.5">
                        <span className="font-mono text-[18px] font-bold text-foreground">
                          {count.toLocaleString(locale)}
                        </span>
                        <span className="text-[11.5px] text-muted-foreground">
                          {share} %
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                        {meta.hint}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-start gap-1.5 text-[11.5px] leading-snug text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span className="text-pretty">{dict.footnote}</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border/70 bg-muted/15 px-4 py-8 text-center">
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {dict.empty.replace('{days}', String(rangeDays))}
              </p>
              <p className="max-w-sm text-sm text-muted-foreground">
                {dict.emptyDescription}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {copyLinkUrl ? (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  className="min-h-10 gap-1.5 rounded-[10px] px-3.5 text-[12.5px] font-semibold"
                  onClick={handleCopyLink}
                >
                  {linkCopied ? (
                    <Check className="size-[15px]" />
                  ) : (
                    <Link2 className="size-[15px]" />
                  )}
                  {linkCopied ? dict.emptyCopied : dict.emptyCopyLink}
                </Button>
              ) : null}
              {walletEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  className="min-h-10 gap-1.5 rounded-[10px] px-3.5 text-[12.5px] font-semibold"
                  disabled={!walletReady || isAddingToWallet}
                  onClick={onAddToWallet}
                >
                  <Wallet className="size-[15px]" />
                  {dict.emptyAddToWallet}
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
